/** Iris-close overlay for the death sequence — a vignette that shrinks to fully cover the screen. */
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, SF0_X, SF0_Y } from '../utils/Constants';

const IRIS_TEX_KEY = '_death-iris';
const IRIS_DEPTH = 10000;

/** Radial vignette iris that slowly closes onto the player corpse during the death sequence. */
export class DeathIrisEffect {
  private scene: Phaser.Scene;
  private canvasTex: Phaser.Textures.CanvasTexture | null = null;
  private overlay: Phaser.GameObjects.Image | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Animate the vignette iris closing from viewport edges to fully opaque at center.
   * Uses the same radial gradient shape as the low-HP vignette overlay.
   * Resolves when the iris is fully closed (screen completely covered).
   */
  close(worldX: number, worldY: number, durationMs: number): Promise<void> {
    this.cleanup();

    if (this.scene.textures.exists(IRIS_TEX_KEY)) {
      this.scene.textures.remove(IRIS_TEX_KEY);
    }
    this.canvasTex = this.scene.textures.createCanvas(IRIS_TEX_KEY, GAME_WIDTH, GAME_HEIGHT);

    this.overlay = this.scene.add.image(
      SF0_X + GAME_WIDTH / 2, SF0_Y + GAME_HEIGHT / 2, IRIS_TEX_KEY,
    )
      .setDisplaySize(GAME_WIDTH, GAME_HEIGHT)
      .setScrollFactor(0)
      .setDepth(IRIS_DEPTH)
      .setOrigin(0.5);

    // Start radius: 0.7 gradient stop lands at the viewport corners so the
    // initial frame looks like the low-HP vignette at full intensity
    const halfDiag = Math.sqrt(GAME_WIDTH * GAME_WIDTH + GAME_HEIGHT * GAME_HEIGHT) / 2;
    const startRadius = halfDiag / 0.7;

    this.drawIris(startRadius, worldX, worldY);

    return new Promise((resolve) => {
      const state = { radius: startRadius };
      this.scene.tweens.add({
        targets: state,
        radius: 0,
        duration: durationMs,
        ease: 'Quad.easeIn',
        onUpdate: () => this.drawIris(state.radius, worldX, worldY),
        onComplete: () => {
          this.drawIris(0, worldX, worldY);
          resolve();
        },
      });
    });
  }

  /** Remove the iris overlay image. */
  remove(): void {
    this.cleanup();
  }

  /** Remove the iris overlay and destroy the backing canvas texture. */
  destroy(): void {
    this.cleanup();
    if (this.scene.textures.exists(IRIS_TEX_KEY)) {
      this.scene.textures.remove(IRIS_TEX_KEY);
    }
  }

  private drawIris(radius: number, worldX: number, worldY: number): void {
    if (!this.canvasTex) return;

    const cam = this.scene.cameras.main;
    const cx = worldX - cam.worldView.x;
    const cy = worldY - cam.worldView.y;

    const ctx = this.canvasTex.context;
    ctx.clearRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    if (radius <= 0) {
      ctx.fillStyle = 'rgb(128, 0, 0)';
      ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
      this.canvasTex.refresh();
      return;
    }

    // Heavy dither — only a tiny clear center, red feathers across nearly
    // the entire radius with many intermediate opacity stops.
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    grad.addColorStop(0, 'rgba(128,0,0,0)');
    grad.addColorStop(0.05, 'rgba(128,0,0,0)');
    grad.addColorStop(0.15, 'rgba(128,0,0,0.08)');
    grad.addColorStop(0.25, 'rgba(128,0,0,0.18)');
    grad.addColorStop(0.35, 'rgba(128,0,0,0.3)');
    grad.addColorStop(0.45, 'rgba(128,0,0,0.42)');
    grad.addColorStop(0.55, 'rgba(128,0,0,0.55)');
    grad.addColorStop(0.65, 'rgba(128,0,0,0.68)');
    grad.addColorStop(0.75, 'rgba(128,0,0,0.8)');
    grad.addColorStop(0.85, 'rgba(128,0,0,0.9)');
    grad.addColorStop(1, 'rgba(128,0,0,1)');

    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.canvasTex.refresh();
  }

  private cleanup(): void {
    if (this.overlay) {
      this.overlay.destroy();
      this.overlay = null;
    }
  }
}
